import { gql } from '@apollo/client';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { formatExperimentsForEventAttributes, userContextIsTrackable } from '@reverbdotcom/commons/src/elog/mparticle';
import { nestedFilterFragment } from '@reverbdotcom/commons/src/components/rql_facets';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { mParticleListingFields } from '@reverbdotcom/commons/src/components/with_mparticle_listings';
import experiments from '@reverbdotcom/commons/src/experiments';
import { experimentInt } from '@reverbdotcom/commons/src/user_context_helpers';

import {
  ListingsCollection as ListingsCollectionClass,
} from '@reverbdotcom/commons/src/components/listings_collection';
import { IUser } from '@reverbdotcom/commons/src/components/user_context_provider';
import {
  reverb_search_ListingsSearchRequest_Sort,
  reverb_search_ListingsSearchRequest_Collapsible,
  CPListingsRecommendationsQueryVariables,
} from '@reverbdotcom/commons/src/gql/graphql';
import { EVERYWHERE_CODE } from '../shared/constants';

export {
  mapParams,
  updateCategoryAsParams,
} from '../marketplace/map_params';

export type {
  MeRecommendedListingsGrid,
  MeRecommendedListings,
  LoggedOutRecommendations,
} from '@reverbdotcom/commons/src/gql/graphql';

export {
  withUserContext,
} from '@reverbdotcom/commons/src/components/user_context_provider';

export const COMPONENT_NAME = 'RecommendedListings';

export const MIN_COUNT = 1;

export function useRecommendedListingsTracking(user: Partial<IUser>, loading: boolean, listingIds = [], recommendationEngineInputIds = [], recommendationEngineOutputIds = [], componentName = COMPONENT_NAME) {
  const mParticleViewData = {
    componentName: componentName,
    eventName: MParticleEventName.ComponentView,
    experiments: formatExperimentsForEventAttributes(
      user,
      [experiments.CP_RECOMMENDATIONS, experiments.LOGGED_OUT_HOMEPAGE_REDESIGN],
    ),
    listingId: listingIds?.join('|'),
    listingsCount: listingIds.length,
    recommendationEngineInputIds: recommendationEngineInputIds?.join('|'),
    recommendationEngineOutputIds: recommendationEngineOutputIds?.join('|'),
  };

  useViewTracking(mParticleViewData, !loading);
}

export const gridQuery = gql`
  query MeRecommendedListingsGrid(
    $query: String
    $currency: String
    $priceMax: String
    $priceMin: String
    $decades: [String]
    $yearMax: String
    $yearMin: String
    $sortSlug: String
    $limit: Int
    $offset: Int
    $categorySlugs: [String]
    $brandSlugs: [String]
    $aggs: [reverb_search_ListingsSearchRequest_Aggregation]
    $conditionSlugs: [String]
    $onSale: Boolean
    $handmade: Boolean
    $freeShipping: Boolean
    $freeExpeditedShipping: Boolean
    $acceptsOffers: Boolean
    $acceptsGiftCards: Boolean
    $preferredSeller: Boolean
    $acceptsPaymentPlans: Boolean
    $itemRegion: String
    $shippingRegionCode: [String]
    $showOnlySold: Boolean
    $showSold: Boolean
    $traitValues: [String]
    $itemState: [String]
    $itemCity: [String]
    $shopSlug: String
    $curatedSetSlugs: [String]
    $saleSlugs: [String]
    $cspSlug: String
    $sort: reverb_search_ListingsSearchRequest_Sort
    $combinedShipping: Boolean
    $acceptsAffirm: Boolean
    $terminateEarly: Boolean
    $collapsible: reverb_search_ListingsSearchRequest_Collapsible
    $fallbackToOr: Boolean
    $withProximityFilter: Input_reverb_search_ProximityFilterRequest
    $localPickup: Boolean
    $shouldSkipTracking: Boolean!
    $version: Int
  ) {
    me {
      uuid
      recommendedListings(
        input: {
          query: $query
          currency: $currency
          priceMax: $priceMax
          priceMin: $priceMin
          decades: $decades
          yearMax: $yearMax
          yearMin: $yearMin
          sortSlug: $sortSlug
          traitValues: $traitValues
          limit: $limit
          offset: $offset
          categorySlugs: $categorySlugs
          brandSlugs: $brandSlugs
          withAggregations: $aggs
          conditionSlugs: $conditionSlugs
          onSale: $onSale
          handmade: $handmade
          freeShipping: $freeShipping
          freeExpeditedShipping: $freeExpeditedShipping
          acceptsOffers: $acceptsOffers
          acceptsGiftCards: $acceptsGiftCards
          preferredSeller: $preferredSeller
          acceptsPaymentPlans: $acceptsPaymentPlans
          itemRegion: $itemRegion
          shippingRegionCodes: $shippingRegionCode
          showOnlySold: $showOnlySold
          showSold: $showSold
          itemState: $itemState
          itemCity: $itemCity
          shopSlug: $shopSlug
          curatedSetSlugs: $curatedSetSlugs
          saleSlugs: $saleSlugs
          cspSlug: $cspSlug
          sort: $sort
          combinedShipping: $combinedShipping
          acceptsAffirm: $acceptsAffirm
          terminateEarly: $terminateEarly
          collapsible: $collapsible
          fallbackToOr: $fallbackToOr
          withProximityFilter: $withProximityFilter
          localPickup: $localPickup
          listingRecommendationsVersion: $version
        }
      ) {
        total
        offset
        limit
        filters {
          ... NestedFilter
        }
        listings {
          _id
          ... ListingsCollection
          ... mParticleListingFields @skip(if: $shouldSkipTracking)
        }
      }
    }
  }
  ${ListingsCollectionClass.fragments.listing}
  ${mParticleListingFields}
  ${nestedFilterFragment}
`;

export const rowQuery = gql`
  query MeRecommendedListings(
    $limit: Int
    $offset: Int
    $shouldSkipTracking: Boolean!
    $version: Int
    $multiClientExperiments: [Input_reverb_search_ExperimentData]
  ) {
    me {
      uuid
      recommendedListings(
        input: {
          limit: $limit
          offset: $offset
          listingRecommendationsVersion: $version
          multiClientExperiments: $multiClientExperiments
        }
      ) {
        listings {
          _id
          ... ListingsCollection
          ... mParticleListingFields @skip(if: $shouldSkipTracking)
        }
      }
    }
  }
  ${ListingsCollectionClass.fragments.listing}
  ${mParticleListingFields}
`;

export const CPRecommendationsRowQuery = gql`
  query CPListingsRecommendations(
    $listingsLimit: Int
    $recsLimit: Int
    $shouldSkipTracking: Boolean!
    $sort: reverb_search_ListingsSearchRequest_Sort
    $collapsible: reverb_search_ListingsSearchRequest_Collapsible
    $shippingRegionCodes: [String]
    $multiClientExperiments: [Input_rql_ExperimentData]
  ) {
    cpToListingRecs(input: {
      limit: $recsLimit
      multiClientExperiments: $multiClientExperiments
      listingsSearchRequest: {
        limit: $listingsLimit
        sort: $sort
        collapsible: $collapsible
        shippingRegionCodes: $shippingRegionCodes
      }
    }) {
      recommendationEngineInputIds
      recommendationEngineOutputIds
      searchResponse {
        limit
        total
        listings {
          _id
          ... ListingsCollection
          ... mParticleListingFields @skip(if: $shouldSkipTracking)
        }
      }
    }
  }
  ${ListingsCollectionClass.fragments.listing}
  ${mParticleListingFields}
`;

export function cpListingsRecommendationsVariables(
  user: Partial<IUser>, listingsLimit: number, recsLimit: number,
): CPListingsRecommendationsQueryVariables {
  const multiClientExperiments = [experiments.CP_RECOMMENDATIONS];
  const mceData = multiClientExperiments.map(exp => ({ name: exp, group: `${experimentInt(user, exp)}` }));

  return {
    listingsLimit: listingsLimit,
    recsLimit: recsLimit,
    shouldSkipTracking: !userContextIsTrackable(user),
    sort: reverb_search_ListingsSearchRequest_Sort.PUBLISHED_AT_DESC,
    collapsible: reverb_search_ListingsSearchRequest_Collapsible.CANONICAL_PRODUCT_ID,
    shippingRegionCodes: [user.shippingRegionCode || EVERYWHERE_CODE],
    multiClientExperiments: mceData,
  };
}

export const loggedOutRowQuery = gql`
  query LoggedOutRecommendations(
    $mpid: String
  ) {
    loggedOutRecommendations(
      input:{
        mpid: $mpid
      }
    ){
      resultIds
      listings {
        _id
        ...ListingsCollection
      }
    }
  }
${ListingsCollectionClass.fragments.listing}
`;
